//* Import necessary React hooks
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import the organizationAPI service
import { organizationAPI } from "./organizationAPI";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";

//* Interface for detailed organization information
type OrganizationsDetails = {
  id: string;
  name: string;
  logoPath: string | null;
  description: string;
  responsibleId: string;
  responsible: {
    id: string;
    name: string;
    email: string;
    role: {
      slug: string;
      name: string;
    };
  };
  createdAt: string;
  updatedAt: string;
};


//* Add type for admin information
type Admins = {
  id: string;
  person: {
    id: string;
    name: string;
    email: string;
  };
  organization: {
    id: string;
    name: string;
  };
  role: string;
  createdAt: string;
  updatedAt: string;
};

//* Hook to manage organization-related operations
export const useOrganizations = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "organizations",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  //* Fetch Organizations (list and search)
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      organizationAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const organizations = data?.organizations || [];
  const fetchedPagination = data?.pagination || pagination;

  //* Handle search input changes and trigger search
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  //* Change page and fetch Organizations for the new page
  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  //* Create a new organization
  const createOrganizations = useMutation({
    mutationFn: async (organizationData: FormData) => {
      const organizationDataObj = Object.fromEntries(
        organizationData.entries()
      ) as {
        name: string;
        description: string;
        logoPath: File | null;
        adminName: string;
        adminEmail: string;
      };
      return organizationAPI.create(organizationDataObj, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  //* Update an organization
  const updateOrganizations = useMutation({
    mutationFn: async ({
      id,
      organizationData,
    }: {
      id: string;
      organizationData: FormData;
    }) => {
      const organizationDataObj = Object.fromEntries(
        organizationData.entries()
      ) as {
        name: string;
        description: string;
        logoPath: File | null;
        responsibleId: string;
      };
      return organizationAPI.update(id, organizationDataObj, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  //* Delete an organization
  const deleteOrganizations = useMutation({
    mutationFn: (id: string) => organizationAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  //* Fetch a specific organization by ID
  const getOrganizationsById = useCallback(
    async (id: string): Promise<OrganizationsDetails | null> => {
      try {
        const organizationsDetails = await organizationAPI.getById(id, token);
        return organizationsDetails as OrganizationsDetails;
      } catch (err) {
        console.error("Error fetching organization by ID:", err);
        return null;
      }
    },
    [token]
  );

  //* Fetch admins by organization
  const getAdminsByOrganization = useCallback(
    async (organizationId: string): Promise<Admins[]> => {
      try {
        const response = await organizationAPI.getAdminsByOrganization(
          organizationId,
          token
        );
        return response;
      } catch (err) {
        console.error("Error fetching admins:", err);
        return [];
      }
    },
    [token]
  );

  return {
    organizations,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createOrganization: createOrganizations.mutate,
    updateOrganization: updateOrganizations.mutate,
    deleteOrganization: deleteOrganizations.mutate,
    getOrganizationsById,
    getAdminsByOrganization,
    changePage,
    isCreating: createOrganizations.isPending,
    isUpdating: updateOrganizations.isPending,
    isDeleting: deleteOrganizations.isPending,
  };
};
